import React, { useState, useContext, useMemo, useRef, useLayoutEffect } from "react";
import { AuthContext } from "../AuthContext";
import { generateSlug } from "../utils/slug";
import { getImageUrl } from "../utils/imageUtils";
import { Link } from "react-router-dom";
import AdImage from "./AdImage/AdImage";
import { useSwipeable } from "react-swipeable";
import { FaMapMarkerAlt, FaHeart, FaRegHeart } from "react-icons/fa";
import Swal from "sweetalert2";
import "./AdCard.css";

function AdCard({ id, images = [], category, title, location, price, created_at, viewMode, isInsideCarousel = false }) {
  const { user } = useContext(AuthContext);
  const [isFavorite, setIsFavorite] = useState(false);

  const [isHovered, setIsHovered] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const categoryContainerRef = useRef(null);
  const categoryTextRef = useRef(null);
  const [isCategoryOverflowing, setIsCategoryOverflowing] = useState(false);

  const adUrl = `/oglas/${generateSlug(title)}/${id}`;

  const isMobile = useMemo(() => {
    // A simple way to check for touch devices.
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }, []);

  useLayoutEffect(() => {
    const checkOverflow = () => {
      if (categoryTextRef.current && categoryContainerRef.current) {
        const isOverflowing = categoryTextRef.current.scrollWidth > categoryContainerRef.current.clientWidth;
        setIsCategoryOverflowing(isOverflowing);
      }
    };

    // Preveri takoj in ob spremembi velikosti okna
    checkOverflow();
    window.addEventListener('resize', checkOverflow);

    // Počisti listener ob uničenju komponente
    return () => window.removeEventListener('resize', checkOverflow);
  }, [category]); // Ponovno preveri, če se spremeni besedilo kategorije

  // Memoize the image array to prevent re-calculations
  const orderedImages = useMemo(() => {
    // === SPREMEMBA: Če ni slik, vrnemo prazen array, AdImage bo poskrbel za placeholder ===
    if (!images || images.length === 0) return [];
    return [...images].sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0));
  }, [images]);

  const totalImages = orderedImages.length;
  //const showSlider = isHovered && totalImages > 1 && viewMode === 'grid';

  const handleNextImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex + 1) % totalImages);
  };

  const handlePrevImage = () => {
    setCurrentImageIndex((prevIndex) => (prevIndex - 1 + totalImages) % totalImages);
  };

  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => handleNextImage(),
    onSwipedRight: () => handlePrevImage(),
    preventDefaultTouchmoveEvent: true, // Prevents page scrolling while swiping
    trackMouse: false, // We only want this for touch, not for mouse dragging
    disabled: isInsideCarousel || totalImages <= 1,
  });

  const showSlider = (isHovered || isMobile) && totalImages > 1 && (viewMode === 'grid' || viewMode === 'list' || isInsideCarousel);

  const handleArrowClick = (e, direction) => {
    e.stopPropagation();
    e.preventDefault();
    if (direction === 'next') {
      handleNextImage();
    } else {
      handlePrevImage();
    }
  };

  const handleFavoriteClick = async (e) => {
    e.preventDefault();

    try {
      if (!user?.id) {
        Swal.fire("Niste prijavljeni.", "Za to dejanje se prosimo prijavite v svoj račun.", "warning");
        return;
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/user/${user.id}/favorites/${id}`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
        }
      );

      if (response.ok) {
        setIsFavorite(true);
        Swal.fire("Uspeh!", "Oglas je shranjen med priljubljene.", "success");
      } else {
        const errorData = await response.json();

        if (response.status === 409) {
          Swal.fire("Opozorilo", errorData.message || "Ta oglas je že med priljubljenimi.", "info");
        } else {
          Swal.fire("Napaka", errorData.message || "Neuspelo dodajanje med priljubljene.", "error");
        }
      }
    } catch (error) {
      console.error("Error adding favorite:", error);
      alert("Napaka pri shranjevanju oglasa.");
    }
  };

  const currentImageObject = orderedImages[currentImageIndex];

  return (
    <div
      className="modern-item-card"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        if (!isMobile) {
          setCurrentImageIndex(0);
        }
      }}
    >
      <Link to={adUrl} className="card-link">
        {/* --- MODIFIED: Spread the swipe handlers here --- */}
        <div className="card-image-wrapper" {...swipeHandlers}>
          <AdImage
            image={currentImageObject}
            variant="card"
            alt={title || "Slika oglasa"}
          />
          <div ref={categoryContainerRef} className="card-category">
            <span ref={categoryTextRef} className={`category-text-inner ${isCategoryOverflowing ? 'is-overflowing' : ''}`}>
              {category}
            </span>
          </div>
          <button className="favorite-button" onClick={handleFavoriteClick}>
            {isFavorite ? <FaHeart color="#FF4367" /> : <FaRegHeart />}
          </button>

          {/* --- MODIFIED: Use new handler for arrows --- */}
          {/* The visibility is now controlled by the updated 'showSlider' variable */}
          {showSlider && (
            <>
              <button className="slider-arrow prev" onClick={(e) => handleArrowClick(e, 'prev')}>‹</button>
              <button className="slider-arrow next" onClick={(e) => handleArrowClick(e, 'next')}>›</button>
              <div className="slider-dots">
                {orderedImages.map((_, index) => (
                  <span key={index} className={`dot ${index === currentImageIndex ? 'active' : ''}`} />
                ))}
              </div>
            </>
          )}
        </div>

        <div className="card-content">
          <div className="card-body">
            <h3 className="card-title">{title}</h3>
            <div className="card-location">
              <FaMapMarkerAlt className="location-icon" />
              {location?.city || "Lokacija ni na voljo"}
            </div>
          </div>
          <div className="card-footer">
            <span className="card-price">{price} €</span>
            <span className="card-date">
              {new Date(created_at).toLocaleDateString("sl-SI")}
            </span>
          </div>
        </div>
      </Link>
    </div>
  );
}

export default React.memo(AdCard);